// src/services/adminService.ts

import { API_ENDPOINT } from '../models/constants';
import type { ManagedUser, AdminApiError } from '../models/adminTypes';

export interface IAdminService {
  listUsers(idToken: string): Promise<ManagedUser[]>;
  createUser(idToken: string, email: string): Promise<{ userId: string }>;
  deleteUser(idToken: string, userId: string): Promise<void>;
  disableUser(idToken: string, userId: string): Promise<void>;
  enableUser(idToken: string, userId: string): Promise<void>;
  resetPassword(idToken: string, userId: string): Promise<{ message: string; tempPassword?: string; loginUrl?: string }>;
}

export class AdminService implements IAdminService {
  private baseUrl = `${API_ENDPOINT}/admin`;

  private async request<T>(
    idToken: string,
    method: string,
    path: string,
    body?: unknown
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw { message: error.message, statusCode: response.status } as AdminApiError;
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  async listUsers(idToken: string): Promise<ManagedUser[]> {
    const data = await this.request<{ users: ManagedUser[] }>(idToken, 'GET', '/users');
    return data.users;
  }

  async createUser(idToken: string, email: string): Promise<{ userId: string }> {
    return this.request<{ userId: string }>(idToken, 'POST', '/users', { email });
  }

  async deleteUser(idToken: string, userId: string): Promise<void> {
    return this.request<void>(idToken, 'DELETE', `/users/${userId}`);
  }

  async disableUser(idToken: string, userId: string): Promise<void> {
    return this.request<void>(idToken, 'POST', `/users/${userId}/disable`);
  }

  async enableUser(idToken: string, userId: string): Promise<void> {
    return this.request<void>(idToken, 'POST', `/users/${userId}/enable`);
  }

  async resetPassword(idToken: string, userId: string): Promise<{ message: string; tempPassword?: string; loginUrl?: string }> {
    return this.request<{ message: string; tempPassword?: string; loginUrl?: string }>(idToken, 'POST', `/users/${userId}/reset-password`);
  }
}
